import React from 'react';
import { Button } from '../../ui/button';
import type { Statistics } from '../types';

interface UploadSectionProps {
  statistics: Statistics | null;
  employees: Array<unknown>;
  importProgress: string;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onDownloadTemplate: () => void;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onExport: () => void;
  onExportHtmlReport: () => void;
  onAutoMap: () => void;
  onResetAll: () => void;
}

export const UploadSection: React.FC<UploadSectionProps> = ({
  statistics,
  employees,
  importProgress,
  fileInputRef,
  onDownloadTemplate,
  onFileSelect,
  onExport,
  onExportHtmlReport,
  onAutoMap,
  onResetAll,
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Employee Mapping</h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            Map employee information to API keys for better tracking and management
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Button
            onClick={onDownloadTemplate}
            variant="outline"
            className="text-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Template
          </Button>

          <Button
            onClick={() => fileInputRef.current?.click()}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Import CSV
          </Button>

          <Button
            onClick={onExport}
            variant="outline"
            className="text-sm"
            disabled={employees.length === 0}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </Button>

          <Button
            onClick={onExportHtmlReport}
            variant="outline"
            className="text-sm bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-700 dark:text-purple-400"
            disabled={employees.length === 0}
            title="Export HTML report grouped by headquarters"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export HTML Report
          </Button>

          <Button
            onClick={onAutoMap}
            variant="outline"
            className="text-sm"
            disabled={employees.length === 0 || importProgress !== ''}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            Auto-Map to API Keys
          </Button>

          <Button
            onClick={onResetAll}
            variant="outline"
            className="text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            disabled={employees.length === 0}
            title="Reset all employee data"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Reset
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={onFileSelect}
            className="hidden"
          />
        </div>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
          <div className="bg-gray-50 dark:bg-gray-900 p-2.5 sm:p-4 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Total Employees</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{statistics.totalEmployees}</div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-2.5 sm:p-4 rounded-lg">
            <div className="text-xs sm:text-sm text-green-600 dark:text-green-400">Mapped to API Keys</div>
            <div className="text-xl sm:text-2xl font-bold text-green-900 dark:text-green-100">{statistics.mappedEmployees}</div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-2.5 sm:p-4 rounded-lg">
            <div className="text-xs sm:text-sm text-blue-600 dark:text-blue-400">Departments</div>
            <div className="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-100">{statistics.departments.length}</div>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 p-2.5 sm:p-4 rounded-lg">
            <div className="text-xs sm:text-sm text-purple-600 dark:text-purple-400">Roles</div>
            <div className="text-xl sm:text-2xl font-bold text-purple-900 dark:text-purple-100">{statistics.roles.length}</div>
          </div>
        </div>
      )}

      {/* Last import info */}
      {statistics?.lastImport && (
        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Last import: {new Date(statistics.lastImport.importedAt).toLocaleString()} -
            {' '}{statistics.lastImport.fileName} ({statistics.lastImport.matchedRecords}/{statistics.lastImport.totalRecords} matched)
          </div>
        </div>
      )}
    </div>
  );
};
